import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'

const LE_PROPERTY_ID_PREFIX = 'le:'

export type OneClickBookingRestorationFlashHotelItem = App.Checkout.InstantBookingLEHotelItem

export function normaliseLePropertyId(propertyId: string | undefined): string | undefined {
  if (!propertyId) return undefined
  if (propertyId.startsWith(LE_PROPERTY_ID_PREFIX)) {
    return propertyId.slice(LE_PROPERTY_ID_PREFIX.length)
  }
  return propertyId
}

export function isOneClickBookingRestorationFlashHotelItem(
  item: App.Checkout.AnyItem | undefined,
): item is OneClickBookingRestorationFlashHotelItem {
  return !!item && isInstantBookingLEHotelItem(item) && !!item.checkIn && !!item.checkOut
}

export function isOneClickBookingRestorationFlashHotelOffer(
  offer: App.AnyOffer | undefined,
): offer is App.HotelOffer {
  return isFlashHotelOffer(offer) && 'packages' in offer
}

export function findFirstCheckoutCartFlashHotelItem(cart: App.SpoofUserCart | undefined): OneClickBookingRestorationFlashHotelItem | undefined {
  return cart?.rawItems.find(isOneClickBookingRestorationFlashHotelItem)
}

export function getCheckoutCartFlashHotelPackageUniqueKey(item: OneClickBookingRestorationFlashHotelItem): string {
  return getPackageUniqueKey(item.packageId, item.duration, item.roomRateId)
}

function doOccupantsMatch(
  searchOccupants: App.Occupants | App.RoomOccupants | undefined,
  cartOccupants: App.Occupants | App.RoomOccupants | undefined,
): boolean {
  if (!searchOccupants || !cartOccupants) return false

  const searchChildrenAge = searchOccupants.childrenAge ?? []
  const cartChildrenAge = cartOccupants.childrenAge ?? []

  return searchOccupants.adults === cartOccupants.adults &&
    searchOccupants.children === cartOccupants.children &&
    searchOccupants.infants === cartOccupants.infants &&
    searchOccupants.teenagers === cartOccupants.teenagers &&
    searchChildrenAge.length === cartChildrenAge.length &&
    searchChildrenAge.every((age, index) => age === cartChildrenAge[index])
}

export function doDatesMatch(filters: App.OfferListFilters, item: OneClickBookingRestorationFlashHotelItem): boolean {
  return filters.checkIn === item.checkIn && filters.checkOut === item.checkOut
}

export function doTravellersMatch(filters: App.OfferListFilters, item: OneClickBookingRestorationFlashHotelItem): boolean {
  if (filters.rooms?.length !== 1) return false
  return doOccupantsMatch(filters.rooms[0], item.occupancy)
}

export function doesDestinationMatch(
  filters: App.OfferListFilters,
  offer: App.HotelOffer,
  locations: Array<App.SearchDest> = [],
): boolean {
  if (!offer.property?.id) return false

  const searchPropertyId = normaliseLePropertyId(filters.propertyId)
  if (searchPropertyId) {
    return searchPropertyId === offer.property.id
  }

  const cartHotelPlaceIds = new Set(locations.map(location => location.place_id))
  const searchPlaceIds = [
    filters.destinationId,
    ...(filters.destinationIds ?? []),
    filters.landmarkId,
    ...(filters.landmarkIds ?? []),
  ].filter((placeId): placeId is string => !!placeId)

  return searchPlaceIds.some(placeId => cartHotelPlaceIds.has(placeId))
}
